import AppStateService from '@ajs/services/AppStateService';
import DatabasesDataService from '@ajs/services/DatabasesDataService';
import FdxUI from '@ajs/services/fdxUI';
import { AfterViewInit, Component, OnDestroy, OnInit, Self } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NG_BOOTSTRAP_OPEN_DELAY } from '@app/core/models/constants/app.constants';
import { AppLayout } from '@app/core/models/enums/app-layout.enum';
import { NavMenuMode } from '@app/core/models/enums/nav-menu-mode.enum';
import { UserFeatureFlag } from '@app/core/models/enums/user-feature-flag.enum';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { AppLayoutService } from '@app/core/services/app-layout.service';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { ErrorService } from '@app/core/services/error.service';
import { LinkService } from '@app/core/services/link.service';
import { DbModel } from '@app/databases/models/db.model';
import { KeyboardShortcutService } from '@app/keyboard-shortcuts/services/keyboard-shortcut.service';
import { ActiveNavStateService } from '@app/modules/nav/services/active-nav-state.service';
import { NgbPopoverConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { asyncScheduler, catchError, EMPTY, filter, from, Subject, switchMap, takeUntil, tap, throttleTime } from 'rxjs';

@Component({
    selector: 'fdx-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    providers: [
        AppLayoutService
    ],
    standalone: false
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
    public viewHeight: boolean = false;
    private readonly updateCurrentDatabase$: Subject<string> = new Subject<string>();
    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    protected modeType: typeof NavMenuMode = NavMenuMode;
    protected mode: NavMenuMode;

    constructor(
        @Self() private readonly appLayoutService: AppLayoutService,
        private readonly analyticsService: AnalyticsService,
        protected readonly appStateService: AppStateService,
        protected readonly bigcommerceSelfService: BigCommerceModeStateService,
        private readonly databasesDataService: DatabasesDataService,
        private readonly errorService: ErrorService,
        private readonly fdxUI: FdxUI,
        private readonly keyboardShortcut: KeyboardShortcutService,
        private readonly linkService: LinkService,
        private readonly activeNavState: ActiveNavStateService,
        private readonly ngbTooltipConfig: NgbTooltipConfig,
        private readonly ngbPopoverConfig: NgbPopoverConfig,
        private readonly router: Router
    ) { }

    ngOnInit(): void {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationStart),
            tap((event: NavigationStart) => {
                // Skip emission when navigating TO the databases page.
                // The databases page manages its own database state (clears then sets a new DB).
                // Emitting here would open a throttle window with a stale ID, blocking the
                // more important emission that fires when navigating AWAY with the new DB.
                const databasesUrl = this.router.createUrlTree(this.linkService.databasesLink).toString();
                if (event.url.split('?')[0] === databasesUrl) {
                    return;
                }
                // Defer to next microtask: RouterLink's navigation fires before
                // the template (click) handler that calls setDatabase(). By deferring,
                // we ensure the databases page's click handler has updated the database
                // state before we read it.
                queueMicrotask(() => {
                    this.updateCurrentDatabase$.next(this.appStateService.getDatabaseId());
                });
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                tap(() => {
                    this.analyticsService.identify();
                    this.mode = this.getNavMenuMode();
                }),
                takeUntil(this.unsubscribe$)
            ).subscribe();

        this.appLayoutService.layout$
            .pipe(
                tap((layout) => {
                    this.viewHeight = layout === AppLayout.ViewHeight;
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // Makes sure the database object is up to date when changing pages, so that users can
        // easily see the database building status over multiple pages.
        this.updateCurrentDatabase$.pipe(
            filter((dbId) => Boolean(dbId)),
            throttleTime(5000, asyncScheduler, { leading: true, trailing: true }),
            switchMap((dbId) => {
                return from(this.databasesDataService.getOneById(dbId)).pipe(
                    tap((fullDatabase: DbModel) => {
                        // Staleness guard: only apply the response if the current database
                        // still matches what we requested. If another source (e.g. the
                        // databases page) changed or cleared the database while this request
                        // was in-flight, discard the now-stale response to avoid overwriting
                        // the newer state.
                        if (this.appStateService.getDatabaseId() === dbId) {
                            this.appStateService.setDatabase(fullDatabase);
                        }
                    }),
                    catchError((err) => {
                        this.errorService.handleError(err);
                        return EMPTY;
                    })
                )
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe();

        this.setTooltipsDefaultConfig();
        this.setPopoversDefaultConfig();
        this.keyboardShortcut.init();
    }

    ngAfterViewInit(): void {
        this.mode = this.getNavMenuMode();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private setTooltipsDefaultConfig(): void {
        this.ngbTooltipConfig.tooltipClass = 'show';
        this.ngbTooltipConfig.openDelay = NG_BOOTSTRAP_OPEN_DELAY;
    }

    private setPopoversDefaultConfig(): void {
        this.ngbPopoverConfig.openDelay = NG_BOOTSTRAP_OPEN_DELAY;
    }

    private getNavMenuMode(): NavMenuMode {
        const urlsWithoutMenus: string[][] = [
            ['/'],
            this.linkService.connectChannelAuthLink,
            this.linkService.connectChannelConfigLink,
            this.linkService.connectChannelExportUrlLink,
            this.linkService.connectChannelAnalyticsLink
        ];

        // If a Surface user lands on the accounts page, ensure we hide the horizontal nav menu
        if (this.appStateService.getUser()?.hasFeature(UserFeatureFlag.SURFACE_USER, 'enabled')) {
            urlsWithoutMenus.push(this.linkService.accountsLink)
        }

        if (this.activeNavState.areAnyRoutesActive(urlsWithoutMenus, true)) {
            return NavMenuMode.HIDDEN;
        }

        // restricted UI for BigCommerce Self Service
        // have as high as possible to ensure it overrides other conditions
        if (this.bigcommerceSelfService.showRestrictedUI()) {
            return NavMenuMode.VERTICAL;
        }

        /**
         * Show the normal horizontal menu if the Url page does not require an account previously set
         * This is required because we cannot determine account level feature flag for BigCommerce Self Service
         */
        if (this.isUrlWithoutAccountData()) {
            return NavMenuMode.NORMAL;
        }

        if (this.fdxUI.showMinimalEbayUI()) {
            return NavMenuMode.EBAY;
        }

        return NavMenuMode.NORMAL;
    }

    private isUrlWithoutAccountData(): boolean {
        const urlsWithOutAccountData: string[][] = [
            this.linkService.profileLink,
            this.linkService.billingLink,
            this.linkService.releaseNotesLink,
            this.linkService.utilitiesLink,
            this.linkService.cheatSheetLink,
            this.linkService.keyboardShortcutLink,
            this.linkService.ebaySelectionLink,
            this.linkService.accountsLink,
            ['/demos/bootstrap'],
            ['/demos/inputs'],
            ['/demos/ag-grid']
        ];

        return this.activeNavState.areAnyRoutesActive(urlsWithOutAccountData, true);
    }
}
